/** Building blocks for the Enterprise OS Home. */
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, Bell, BookOpen, CheckCircle2, ChevronRight, CreditCard,
  ExternalLink, FileText, Inbox, LifeBuoy, Package, Receipt, RefreshCw, Rocket,
  ScrollText, Stamp, Truck, Wallet, Warehouse, ShieldAlert, Flame, Clock, Activity,
} from "lucide-react";

const ICONS = { FileText, Inbox, Package, Rocket, Truck, Warehouse, Stamp, LifeBuoy, Wallet,
  CreditCard, Receipt, AlertTriangle, ScrollText, BookOpen };

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtVal = (t) => t.fmt === "inr" ? fmtINR(t.value) : Number(t.value || 0).toLocaleString("en-IN");
const ago = (t) => {
  if (!t) return "";
  const m = Math.floor((Date.now() - new Date(t).getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h`;
  return `${Math.floor(m / 1440)}d`;
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const PRIO = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-50 text-slate-600 border-slate-200",
};
const TONE = { good: "text-emerald-700", warn: "text-amber-600", bad: "text-rose-700" };

export const SectionLabel = ({ step, title, count, hint, onMore, moreLabel }) => (
  <div className="flex items-end gap-3 mb-3">
    <span className="font-mono text-[11px] tracking-[0.2em] text-sky-600 font-semibold pb-1">{step}</span>
    <h2 className="font-heading text-base md:text-lg font-bold tracking-tight text-slate-900 uppercase">{title}</h2>
    {count != null && count > 0 && (
      <span className="text-[11px] font-mono font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-0.5 mb-0.5">{count}</span>
    )}
    {hint && <span className="text-xs text-slate-400 pb-0.5 hidden sm:inline">{hint}</span>}
    {onMore && (
      <button onClick={onMore} data-testid={`os-more-${title.replace(/\s+/g, "-").toLowerCase()}`}
        className="ml-auto text-xs font-medium text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
        {moreLabel} <ArrowRight className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

export function TodayHeader({ user, date, counts, refreshedAt, onRefresh }) {
  const navigate = useNavigate();
  const first = (user.name || user.email || "").split(/[ @]/)[0];
  const day = new Date(date).toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long" });
  const chips = [
    { key: "action_items", label: "Action items", value: counts.action_items, route: "/today", icon: Flame, tone: counts.action_items ? "warn" : null },
    { key: "approvals", label: "Approvals", value: counts.approvals, route: "/approvals/one", icon: Stamp },
    { key: "exceptions", label: "Exceptions", value: counts.exceptions, route: "/exception-centre", icon: ShieldAlert, tone: counts.exceptions ? "bad" : null },
    { key: "escalations", label: "Escalations", value: counts.escalations, route: "/exception-centre", icon: AlertTriangle, tone: counts.escalations ? "bad" : null },
    { key: "unread", label: "Unread", value: counts.unread_notifications, route: "/today", icon: Bell },
  ];
  return (
    <header className="rounded-xl bg-slate-900 text-white px-5 sm:px-7 py-6 sm:py-8 relative overflow-hidden" data-testid="os-today-header">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #29ABE2 0, transparent 40%), radial-gradient(circle at 80% 70%, #f59e0b 0, transparent 35%)" }} />
      <div className="relative flex flex-wrap items-start gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[11px] tracking-[0.2em] text-sky-300 font-semibold">01 · TODAY · {day}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1" data-testid="os-greeting">
            {greeting()}, {first}.
          </h1>
          <p className="text-sm text-slate-300 mt-1">
            {user.role_label} · SUNTRONIX ONE Operating System
          </p>
        </div>
        <button onClick={onRefresh} data-testid="os-refresh"
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-slate-700 rounded-md px-2.5 h-8">
          <RefreshCw className="h-3.5 w-3.5" /> {refreshedAt ? `Updated ${ago(refreshedAt)}` : "Refresh"}
        </button>
      </div>
      <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {chips.map((c) => (
          <button key={c.key} onClick={() => navigate(c.route)} data-testid={`os-chip-${c.key}`}
            className="text-left rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 px-3 py-2.5 transition-colors">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400">
              <c.icon className="h-3.5 w-3.5" /> {c.label}
            </div>
            <div className={`font-heading text-2xl font-bold mt-0.5 ${c.tone === "bad" ? "text-rose-300" : c.tone === "warn" ? "text-amber-300" : "text-white"}`}>
              {c.value ?? 0}
            </div>
          </button>
        ))}
      </div>
    </header>
  );
}

export function AttentionPanel({ attention, onAction, onComplete }) {
  const navigate = useNavigate();
  const items = attention.items || [];
  if (!items.length && !(attention.coming_up || []).length) {
    return (
      <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/40 px-4 py-6 text-sm text-emerald-800 flex items-center gap-2" data-testid="os-attention-empty">
        <CheckCircle2 className="h-4 w-4" /> Nothing needs your attention right now.
      </div>
    );
  }
  return (
    <div className="grid lg:grid-cols-3 gap-3">
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-2.5">
        {items.map((it) => (
          <div key={it.key} data-testid={`os-attn-${it.key}`}
            className="rounded-lg border border-slate-200 bg-white px-3.5 py-3 flex flex-col gap-2 hover:border-slate-300 transition-colors">
            <div className="flex items-start gap-2">
              <span className={`text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 shrink-0 ${PRIO[it.priority] || PRIO.low}`}>{it.priority || "info"}</span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 leading-snug">{it.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{it.reason}{it.due_at && <span className="text-slate-400"> · due {it.due_at}</span>}</div>
              </div>
              {it.value_inr != null && <span className="ml-auto font-mono text-[11px] text-slate-500 shrink-0">{fmtINR(it.value_inr)}</span>}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {(it.actions || []).slice(0, 2).map((a, i) => (
                <button key={i} onClick={() => onAction(a)} data-testid={`os-attn-action-${it.key}-${i}`}
                  className={`h-7 px-2.5 rounded text-[12px] font-medium inline-flex items-center gap-1 ${i === 0 ? "bg-slate-900 text-white hover:bg-slate-800" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                  {a.kind === "route" ? <ExternalLink className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}{a.label}
                </button>
              ))}
              {it.can_complete && (
                <button onClick={() => onComplete(it)} data-testid={`os-attn-done-${it.key}`}
                  className="h-7 px-2 rounded text-[12px] border border-emerald-200 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Done
                </button>
              )}
              <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-400">{it.group}</span>
            </div>
          </div>
        ))}
      </div>
      <aside className="rounded-lg border border-slate-200 bg-slate-50/60 px-3.5 py-3" data-testid="os-coming-up">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          <Clock className="h-3.5 w-3.5" /> Coming up
        </div>
        {(attention.coming_up || []).length === 0
          ? <div className="text-xs text-slate-400">Nothing scheduled ahead.</div>
          : attention.coming_up.map((c) => (
            <button key={c.key} onClick={() => c.route && navigate(c.route)} data-testid={`os-coming-${c.key}`}
              className="w-full text-left py-1.5 border-b border-slate-100 last:border-0 hover:bg-white rounded px-1 -mx-1">
              <div className="text-[13px] text-slate-800 truncate">{c.title}</div>
              <div className="text-[11px] text-slate-400">{c.reason}</div>
            </button>
          ))}
      </aside>
    </div>
  );
}

export function ActionPanel({ actions, approvals, onAction }) {
  const navigate = useNavigate();
  return (
    <div className="grid lg:grid-cols-3 gap-3">
      <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="os-quick-actions">
        {actions.map((a) => {
          const Icon = ICONS[a.icon] || ChevronRight;
          return (
            <button key={a.key} onClick={() => navigate(a.route)} data-testid={`os-qa-${a.key}`}
              className="group rounded-lg border border-slate-200 bg-white px-3 py-3 text-left hover:border-sky-300 hover:bg-sky-50/40 transition-colors">
              <Icon className="h-4 w-4 text-slate-500 group-hover:text-sky-700" strokeWidth={1.7} />
              <div className="text-[13px] font-medium text-slate-800 mt-2 leading-tight">{a.label}</div>
            </button>
          );
        })}
      </div>
      <aside className="rounded-lg border border-slate-200 bg-white px-3.5 py-3" data-testid="os-approvals">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          <Stamp className="h-3.5 w-3.5" /> Approvals waiting
        </div>
        {(approvals || []).length === 0
          ? <div className="text-xs text-slate-400">No approvals waiting.</div>
          : approvals.map((a) => (
            <div key={a.key} className="py-1.5 border-b border-slate-100 last:border-0 flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-slate-800 truncate">{a.title}</div>
                <div className="text-[11px] text-slate-400 truncate">{a.reason}</div>
              </div>
              {a.actions?.[0] && (
                <button onClick={() => onAction(a.actions[0])} data-testid={`os-approval-open-${a.key}`}
                  className="text-[11px] text-sky-700 hover:underline shrink-0">{a.actions[0].label}</button>
              )}
            </div>
          ))}
      </aside>
    </div>
  );
}

export function PerformancePanel({ groups }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.label} data-testid={`os-perf-${g.label.toLowerCase()}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{g.label}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {g.tiles.map((t) => (
              <button key={t.key} onClick={() => navigate(t.route)} data-testid={`os-kpi-${t.key}`}
                className="text-left rounded-lg border border-slate-200 bg-white px-4 py-3.5 hover:-translate-y-px hover:shadow-sm hover:border-slate-300 transition-[transform,box-shadow,border-color]">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t.label}</div>
                <div className={`font-heading text-2xl sm:text-3xl font-bold tracking-tighter mt-1 ${TONE[t.tone] || "text-slate-900"}`}>{fmtVal(t)}</div>
                {t.hint && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{t.hint}</div>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function FeedPanel({ feed, isManagement }) {
  const navigate = useNavigate();
  return (
    <div className={`grid gap-3 ${isManagement ? "lg:grid-cols-2" : ""}`}>
      <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3" data-testid="os-feed-notifications">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          <Bell className="h-3.5 w-3.5" /> Notifications
        </div>
        {(feed.notifications || []).length === 0
          ? <div className="text-xs text-slate-400">No notifications yet.</div>
          : feed.notifications.map((n) => (
            <button key={n.id} onClick={() => n.route && navigate(n.route)} data-testid={`os-notif-${n.id}`}
              className="w-full text-left py-1.5 border-b border-slate-100 last:border-0 flex items-start gap-2 hover:bg-slate-50 rounded px-1 -mx-1">
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${n.read ? "bg-slate-200" : "bg-sky-500"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-slate-800 truncate">{n.title}</div>
                <div className="text-[11px] text-slate-400 truncate">{n.body}</div>
              </div>
              <span className="text-[10px] font-mono text-slate-400 shrink-0">{ago(n.at)}</span>
            </button>
          ))}
      </div>
      {isManagement && (
        <div className="rounded-lg border border-slate-200 bg-white px-3.5 py-3" data-testid="os-feed-audit">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <Activity className="h-3.5 w-3.5" /> Audit trail
            </div>
            <button onClick={() => navigate("/audit")} className="text-[11px] text-sky-700 hover:underline" data-testid="os-audit-all">View all</button>
          </div>
          {(feed.audit || []).length === 0
            ? <div className="text-xs text-slate-400">No audit entries.</div>
            : feed.audit.map((a) => (
              <div key={a.id} className="py-1.5 border-b border-slate-100 last:border-0 flex items-center gap-2 text-[12px]">
                <span className="font-mono text-[10px] text-slate-400 w-8 shrink-0">{ago(a.timestamp)}</span>
                <span className="text-slate-800 truncate">{a.user_email}</span>
                <span className="text-slate-500 shrink-0">{a.action}</span>
                <span className="ml-auto text-slate-400 truncate max-w-[40%]">{a.module}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
